import Link from '@/routing/Link'
import { useRouter } from '@/routing/navigation'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ArrowLeft, Loader2, Plus, X } from '@digital-twins/design-system/icons'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AddFilterModal } from '../../components/AddFilterModal'
import { AudienceTagsSection } from '../../components/AudienceTagsSection'
import { DiscardImportSessionDialog } from '../../components/DiscardImportSessionDialog'
import { FilterChipGrid } from '../../components/FilterChipGrid'
import { FilterDetailModal } from '../../components/FilterDetailModal'
import { ImportStepHeader } from '../../components/ImportStepHeader'
import { ImportActionBar } from '../../components/ImportActionBar'
import { SimulatingProgressCard } from '../../components/SimulatingProgressCard'
import { useImportWizardContext } from '../../context/ImportWizardContext'
import { useAudienceImportSessionMutations } from '../../hooks/useAudienceImportSession'
import { usePreviewFilterSelection } from '../../hooks/usePreviewFilterSelection'
import { usePreviewProjection } from '../../hooks/usePreviewProjection'
import { useSimulatingProgress } from '../../hooks/useSimulatingProgress'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import { audienceImportSessionsApi } from '@/lib/api'
import i18n from '@/i18n'
import { AudienceCreationFlowSnapshot, AudienceTagCondition } from '@digital-twins/shared-types'

function getAudienceCreateBlocked(
  snapshot: AudienceCreationFlowSnapshot,
): { message: string; recoverAction?: 'context' | 'identity' } | null {
  if (snapshot.actions.canCreate) {
    return null
  }

  const { branchSummaries } = snapshot
  const preview = branchSummaries.preview
  const create = branchSummaries.create

  if (snapshot.machineState === 'expired') {
    return {
      message: i18n.t('audienceCreate.blockedExpired', { ns: 'app' }),
    }
  }
  if (snapshot.machineState === 'failed_terminal') {
    return {
      message: i18n.t('audienceCreate.blockedTerminal', { ns: 'app' }),
    }
  }
  if (snapshot.machineState === 'completed') {
    return {
      message: i18n.t('audienceCreate.blockedCompleted', { ns: 'app' }),
    }
  }
  if (snapshot.machineState === 'creating') {
    return {
      message: i18n.t('audienceCreate.blockedCreating', { ns: 'app' }),
    }
  }
  if (snapshot.wizardPhase === 'create_running') {
    return {
      message: i18n.t('audienceCreate.blockedCreating', { ns: 'app' }),
    }
  }
  if (preview.readyToCreate) {
    return {
      message: i18n.t('audienceCreate.blockedInProgress', { ns: 'app' }),
    }
  }
  if (
    create.state === 'queued' ||
    create.state === 'creating' ||
    create.state === 'finalizing'
  ) {
    return {
      message: i18n.t('audienceCreate.blockedInProgress', { ns: 'app' }),
    }
  }

  const cm = branchSummaries.contextMaterials
  if (cm.state !== 'completed' && cm.state !== 'skipped') {
    return {
      message: i18n.t('audienceCreate.blockedContext', { ns: 'app' }),
      recoverAction: 'context',
    }
  }
  if (cm.hasPendingIndexing) {
    return {
      message: i18n.t('audienceCreate.blockedIndexing', { ns: 'app' }),
      recoverAction: 'context',
    }
  }
  if (!branchSummaries.identity.readyToCreate) {
    return {
      message: i18n.t('audienceCreate.blockedIdentity', { ns: 'app' }),
      recoverAction: 'identity',
    }
  }

  // Preview branch can lag briefly; preflight is owned by load_and_suggest_demographics.
  // Do not show a "fix demographics" CTA here — identity is already ready.
  if (preview.state === 'not_started') {
    return null
  }

  if (preview.state === 'eligible') {
    return {
      message: i18n.t('audienceCreate.blockedBackground', { ns: 'app' }),
      recoverAction: 'context',
    }
  }

  return {
    message: i18n.t('audienceCreate.blockedGeneric', { ns: 'app' }),
  }
}

export function SegmentationStep() {
  const { t } = useTranslation('app')
  const {
    sessionId,
    snapshot,
    goToStep,
    hiddenFilterColumns,
    setHiddenFilterColumns,
    customFilterLabels,
    setCustomFilterLabels,
    audienceTags,
    setAudienceTags,
  } = useImportWizardContext()

  const {
    projection,
    isLoading: isProjectionLoading,
    error: projectionError,
  } = usePreviewProjection(sessionId)

  const {
    filterChipItems,
    filterSamplesForAddModal,
    columnGroupsForAddFilter,
    filterMappingsForModal,
    activeFilterColumns,
    detailItem,
    setDetailItem,
    detailGroup,
    setDetailGroup,
    addModalOpen,
    setAddModalOpen,
    filterQuestions,
    allQuestions,
    filterGroups,
    removeFilter,
    renameFilter,
    removeGroup,
    renameGroup,
    addQuestion,
  } = usePreviewFilterSelection(projection, {
    persistedLabels: customFilterLabels,
    onLabelsChange: setCustomFilterLabels,
  })

  const router = useRouter()
  const { discard, isDiscarding } = useAudienceImportSessionMutations(sessionId, {
    fallbackMachineVersion: snapshot?.machineVersion,
  })
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false)
  const [hideModalOpen, setHideModalOpen] = useState(false)

  // Collapse hidden columns that belong to the same group into a single chip;
  // ungrouped columns render as individual chips.
  const hiddenFilterEntries = useMemo(() => {
    const hiddenSet = new Set(hiddenFilterColumns)
    const groupMemberColumns = new Set(
      filterGroups.flatMap((g) => g.memberColumns),
    )

    type HiddenEntry =
      | { type: 'single'; sourceColumn: string; label: string }
      | { type: 'group'; groupId: string; label: string; memberColumns: Array<string> }

    const result: Array<HiddenEntry> = []

    for (const group of filterGroups) {
      const hiddenMembers = group.memberColumns.filter((col) => hiddenSet.has(col))
      if (hiddenMembers.length > 0) {
        result.push({ type: 'group', groupId: group.groupId, label: group.label, memberColumns: hiddenMembers })
      }
    }

    for (const col of hiddenFilterColumns) {
      if (groupMemberColumns.has(col)) continue
      const chip = filterChipItems.find((item) => item.sourceColumn === col)
      result.push({ type: 'single', sourceColumn: col, label: chip?.displayLabel ?? col })
    }

    return result
  }, [hiddenFilterColumns, filterGroups, filterChipItems])

  const canCreate = Boolean(snapshot?.actions?.canCreate)
  const createBlocked =
    snapshot && !canCreate ? getAudienceCreateBlocked(snapshot) : null

  const isSegmentationLoading = isProjectionLoading && !projection
  const [loadingStartMs] = useState<number>(() => Date.now())
  const loadingPercent = useSimulatingProgress(isSegmentationLoading, loadingStartMs)

  const tagColumnLabels = useMemo(() => {
    if (!projection?.segmentationMappings) return {}
    const labels: Record<string, string> = {}
    for (const mapping of projection.segmentationMappings) {
      labels[mapping.sourceColumn] = mapping.displayLabel ?? mapping.sourceColumn
    }
    return labels
  }, [projection?.segmentationMappings])

  const tagColumnValueLabels = useMemo(() => {
    if (!projection?.segmentationMappings) return {}
    const map: Record<string, Record<string, string>> = {}
    for (const mapping of projection.segmentationMappings) {
      if (mapping.valueLabels) {
        map[mapping.sourceColumn] = mapping.valueLabels
      }
    }
    return map
  }, [projection?.segmentationMappings])

  const handlePreviewTag = useCallback(
    (
      conditions: Array<AudienceTagCondition>,
      signal: AbortSignal,
    ) => {
      if (snapshot?.machineVersion == null) {
        return Promise.reject(new Error('Import session version unavailable'))
      }
      return audienceImportSessionsApi.previewTag(
        sessionId,
        conditions,
        snapshot.machineVersion,
        signal,
      )
    },
    [sessionId, snapshot?.machineVersion],
  )

  if (snapshot?.preflightStatus === 'pending') {
    return (
      <div className="flex flex-col gap-6 motion-safe:animate-wizard-enter-content">
        <ImportStepHeader
          title={t('audienceCreate.preparingDataset')}
          helper={t('audienceCreate.analyzingColumnsFilters')}
        />
        <p className="mx-auto max-w-2xl text-center text-sm text-muted-foreground -mt-2 px-2">
          {t('audienceCreate.closeTabResumeLater')}
        </p>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-20">
          <Loader2
            className="h-10 w-10 shrink-0 animate-spin text-primary"
            aria-hidden
          />
          <p className="text-center text-base text-muted-foreground">
            {t('audienceCreate.hangTight')}
          </p>
        </div>
      </div>
    )
  }

  if (
    snapshot?.preflightStatus === 'rejected' ||
    snapshot?.preflightStatus === 'failed'
  ) {
    const isRejection = snapshot.preflightStatus === 'rejected'
    const error = snapshot.preflightError
    return (
      <div className="flex flex-col gap-6 motion-safe:animate-wizard-enter-content">
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="-ml-3 self-start text-muted-foreground hover:text-foreground"
        >
          <Link href="/audiences/new">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('audienceCreate.startOver')}
          </Link>
        </Button>
        <ImportStepHeader
          title={
            isRejection
              ? t('audienceCreate.couldNotUseFile')
              : t('audienceCreate.prepareDatasetFailed')
          }
          helper={t('audienceCreate.startOverDifferentFile')}
        />
        <Alert variant="destructive" className="rounded-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error?.message ||
              (isRejection
                ? t('audienceCreate.fileNoUsableRows')
                : t('audienceCreate.analyzeDatasetUnexpected'))}
            {error?.userAction ? <> {error.userAction}</> : null}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (isSegmentationLoading) {
    return (
      <div className="flex flex-col gap-6 motion-safe:animate-wizard-enter-content">
        <ImportStepHeader
          title={t('audienceCreate.buildingAudience')}
          helper={t('audienceCreate.turningRawData')}
        />
        <SimulatingProgressCard
          percent={loadingPercent}
          title={t('audienceCreate.simulatingAudience')}
          subtitle={t('audienceCreate.processingData')}
          dismissHint={t('audienceCreate.dismissHint')}
          resumeHint={t('audienceCreate.resumeHint')}
          helper={t('audienceCreate.usuallyTakes')}
          className="w-full"
        />
      </div>
    )
  }

  if (projectionError || !projection) {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="-ml-3 self-start text-muted-foreground hover:text-foreground"
        >
          <Link href="/audiences/new">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('audienceCreate.startOver')}
          </Link>
        </Button>
        <ImportStepHeader
          title={t('audienceCreate.segmentationUnavailable')}
          helper={t('audienceCreate.sessionLoadFailed')}
        />
        <Alert variant="destructive" className="rounded-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {projectionError instanceof Error
              ? getApiErrorMessage(
                projectionError,
                t('audienceCreate.sessionExpiredOrDiscarded'),
              )
              : t('audienceCreate.sessionExpiredOrDiscarded')}
          </AlertDescription>
        </Alert>
      </>
    )
  }

  async function handleDiscard() {
    try {
      await discard()
      setDiscardDialogOpen(false)
      router.push('/audiences/new')
    } catch { }
  }

  return (
    <>
      <DiscardImportSessionDialog
        open={discardDialogOpen}
        onOpenChange={setDiscardDialogOpen}
        onConfirm={handleDiscard}
        isPending={isDiscarding}
      />

      <div className="flex flex-1 flex-col gap-10 motion-safe:animate-wizard-enter-content">
        <header className="flex flex-col gap-3">
          <h1 className="font-display text-[36px] font-normal leading-[1.2] tracking-[-0.72px] text-foreground">
            {t('audienceCreate.setSegmentation')}
          </h1>
          <div className="text-lg font-normal leading-[1.6] tracking-[-0.36px] text-muted-foreground">
            <p>{t('audienceCreate.defineTagsAndHide')}</p>
          </div>
        </header>

        {/* Audience Tags section */}
        <AudienceTagsSection
          tags={audienceTags}
          onTagsChange={setAudienceTags}
          columnSamples={projection?.filterColumnSamples ?? {}}
          columnLabels={tagColumnLabels}
          columnValueLabels={tagColumnValueLabels}
          questions={allQuestions}
          onPreviewTag={handlePreviewTag}
        />

        {/* Divider */}
        <div className="h-px bg-border" />

        {/* Filters to Hide section */}
        <section className="flex flex-col gap-4" data-testid="filters-to-hide-section">
          <header className="flex flex-col gap-1">
            <h2 className="text-lg font-medium text-foreground">{t('audienceCreate.filtersToHide')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('audienceCreate.filtersToHideHelper')}
            </p>
          </header>

          {hiddenFilterColumns.length > 0 ? (
            <div className="flex items-center gap-3">
              <div className="flex min-w-0 flex-wrap gap-2">
                <TooltipProvider delayDuration={200}>
                  {hiddenFilterEntries.map((entry) => {
                    const key = entry.type === 'group' ? entry.groupId : entry.sourceColumn
                    const tooltip = entry.type === 'group' ? entry.label : entry.sourceColumn
                    return (
                      <Tooltip key={key}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="flex items-center gap-2 rounded-[6px] bg-primary/15 pl-4 pr-2 py-1.5 text-left hover:opacity-80 transition-opacity max-w-[300px]"
                            onClick={() => {
                              if (entry.type === 'group') {
                                const remove = new Set(entry.memberColumns)
                                setHiddenFilterColumns(
                                  hiddenFilterColumns.filter((c) => !remove.has(c)),
                                )
                              } else {
                                setHiddenFilterColumns(
                                  hiddenFilterColumns.filter((c) => c !== entry.sourceColumn),
                                )
                              }
                            }}
                          >
                            <span className="truncate text-base font-medium text-black">
                              {entry.label}
                            </span>
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full hover:bg-black/10 text-black/50 hover:text-black transition-colors">
                              <X className="h-3.5 w-3.5" />
                            </span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start" className="max-w-sm text-[14px] px-4 py-3">
                          {tooltip}
                        </TooltipContent>
                      </Tooltip>
                    )
                  })}
                </TooltipProvider>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => setHideModalOpen(true)}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {t('audienceCreate.hideAColumn')}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => setHideModalOpen(true)}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              {t('audienceCreate.hideAColumn')}
            </Button>
          )}
        </section>

        {createBlocked ? (
          <Alert
            variant="default"
            className="rounded-2xl border-amber-500/40 bg-amber-500/5"
            data-testid="segmentation-create-blocked-alert"
          >
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-foreground">
              <span>{createBlocked.message}</span>
              {createBlocked.recoverAction ? (
                <>
                  {' '}
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto inline p-0 align-baseline"
                    data-testid="segmentation-create-blocked-recover"
                    onClick={() => {
                      const step = createBlocked.recoverAction
                      if (step) {
                        goToStep(step)
                      }
                    }}
                  >
                    {createBlocked.recoverAction === 'context'
                      ? t('audienceCreate.goToContextMaterials')
                      : t('audienceCreate.goToDemographics')}
                  </Button>
                </>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}

        <ImportActionBar
          backLabel={t('audienceCreate.steps.identity')}
          onBack={() => goToStep('identity')}
          nextLabel={t('audienceCreate.reviewConfirm')}
          onNext={() => goToStep('confirm')}
          nextDisabled={!canCreate}
        />
      </div>

      <FilterDetailModal
        item={detailItem}
        sampleValues={
          detailItem
            ? (projection?.filterColumnSamples?.[detailItem.sourceColumn] ?? [])
            : []
        }
        open={detailItem != null || detailGroup != null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailItem(null)
            setDetailGroup(null)
          }
        }}
        onRename={renameFilter}
        group={detailGroup}
        onRenameGroup={renameGroup}
      />

      <AddFilterModal
        open={hideModalOpen}
        onOpenChange={setHideModalOpen}
        questions={filterQuestions}
        onAdd={(question) => {
          const cols = question.options.map((o) => o.sourceColumn)
          const newCols = cols.filter((c) => !hiddenFilterColumns.includes(c))
          if (newCols.length > 0) {
            setHiddenFilterColumns([...hiddenFilterColumns, ...newCols])
          }
        }}
        title={t('audienceCreate.hideAColumn')}
        addButtonLabel={t('audienceCreate.hideColumn')}
      />
    </>
  )
}
